/**
 * Card set logic.
 *
 * The physical deck stays on the table, so the app never deals cards -- a
 * player tells it "Ukraine, cavalry" and it records that. This is why a card
 * carries its symbol rather than looking it up: editions assign symbols to
 * territories differently, and mirroring whatever is physically in someone's
 * hand is more robust than asserting a canonical mapping.
 */

import type { CardId, TerritoryId } from '../ids.js';
import type { CardSymbol, RulesConfig, SetType } from './config.js';

export interface Card {
  readonly id: CardId;
  /** `null` for a wild card. */
  readonly territory: TerritoryId | null;
  readonly symbol: CardSymbol;
}

const PLAIN_SYMBOLS = ['infantry', 'cavalry', 'artillery'] as const;

/**
 * Every set shape these three cards could legally be called, ignoring value.
 * Wilds substitute for any symbol, so a hand can qualify as more than one.
 */
export function possibleSetTypes(cards: readonly Card[]): readonly SetType[] {
  if (cards.length !== 3) return [];

  const wilds = cards.filter((c) => c.symbol === 'wild').length;
  const plain = cards.filter((c) => c.symbol !== 'wild').map((c) => c.symbol);

  if (wilds === 3) return ['wild'];

  const distinct = new Set(plain);
  const types: SetType[] = [];

  // Three of a kind: the non-wild cards must already agree, and wilds fill in.
  if (distinct.size === 1) {
    types.push([...distinct][0] as SetType);
  } else if (wilds === 2) {
    // Two wilds plus one card can become three of that card's kind.
    for (const symbol of distinct) types.push(symbol as SetType);
  }

  // One of each: needs three different plain symbols, with wilds covering gaps.
  if (distinct.size + wilds >= 3 && distinct.size === plain.length) {
    types.push('mixed');
  }

  return types;
}

/**
 * The set shape a trade should be scored as: the most valuable shape the cards
 * can legally claim. Returns `null` when they are not a set at all.
 */
export function classifySet(cards: readonly Card[], rules: RulesConfig): SetType | null {
  const candidates = possibleSetTypes(cards);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0]!;

  const progression = rules.cards.progression;
  if (progression.kind === 'fixed') {
    return candidates.reduce((best, type) =>
      progression.values[type] > progression.values[best] ? type : best,
    );
  }
  // Escalating values ignore the shape, so prefer the specific reading.
  return candidates.find((t) => t !== 'mixed') ?? candidates[0]!;
}

export function isValidSet(cards: readonly Card[], rules: RulesConfig): boolean {
  return classifySet(cards, rules) !== null;
}

/**
 * Armies for the nth set traded. Under escalating rules `tradesCompleted`
 * counts every set traded in the game by anyone, not just by this player.
 */
export function setValue(
  setType: SetType,
  tradesCompleted: number,
  rules: RulesConfig,
): number {
  const progression = rules.cards.progression;
  if (progression.kind === 'fixed') return progression.values[setType];

  const { sequence, increment } = progression;
  if (sequence.length === 0) return increment * (tradesCompleted + 1);
  if (tradesCompleted < sequence.length) return sequence[tradesCompleted]!;

  const last = sequence[sequence.length - 1]!;
  return last + increment * (tradesCompleted - sequence.length + 1);
}

export interface TerritoryBonus {
  /** Territories that earn the bonus, in the order the cards were given. */
  readonly territories: readonly TerritoryId[];
  readonly armiesEach: number;
  readonly totalArmies: number;
}

/**
 * The bonus for trading a card showing a territory you own -- the rule tables
 * most often forget, which is a large part of why this app exists.
 */
export function territoryBonusFor(
  cards: readonly Card[],
  ownedTerritories: ReadonlySet<TerritoryId>,
  rules: RulesConfig,
): TerritoryBonus {
  const eligible = cards
    .map((card) => card.territory)
    .filter((id): id is TerritoryId => id !== null && ownedTerritories.has(id))
    .slice(0, Math.max(0, rules.cards.maxBonusCardsPerTrade));

  return {
    territories: eligible,
    armiesEach: rules.cards.territoryBonus,
    totalArmies: eligible.length * rules.cards.territoryBonus,
  };
}

/** Whether a hand of this size forces a trade before the player may continue. */
export function mustTrade(handSize: number, rules: RulesConfig): boolean {
  return handSize >= rules.cards.forceTradeAt;
}

export { PLAIN_SYMBOLS };
